import { useApiClient } from 'bff/ApiClientProvider';
import {
  Result,
  ResultStates,
  TrustDetailsGetTrustDetailsRequest,
  TrustDetailsPostRequest
} from 'bff/client';
import { secondsToMilliseconds } from 'date-fns';
import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { UseDisplayErrorMutationOptions } from 'bff/types/display-error-props.type';
import { useInvalidateClientDetails } from 'bff/hooks/quotes/ClientDetailsApi';
import { useInvalidateProposalInformation } from 'bff/hooks/common/ProposalsInfosApi';

export const trustDetailsKeys = {
  base: () => ['personal-details-trust'] as const,
  fetch: (request: TrustDetailsGetTrustDetailsRequest) => [...trustDetailsKeys.base(), request],
  submit: () => [...trustDetailsKeys.base(), 'submit'] as const
};

export const useInvalidateTrustDetails = () => {
  const queryClient = useQueryClient();

  return () =>
    queryClient.invalidateQueries({
      queryKey: trustDetailsKeys.base(),
      refetchType: 'all'
    });
};

export const getTrustDetailsOptions = (
  getTrustDetailsViewRequest: TrustDetailsGetTrustDetailsRequest
) => {
  const { trustDetailsApi } = useApiClient();
  return queryOptions({
    staleTime: secondsToMilliseconds(30),
    queryKey: trustDetailsKeys.fetch(getTrustDetailsViewRequest),
    queryFn: () => trustDetailsApi.trustDetailsGetTrustDetails(getTrustDetailsViewRequest),
    select: (response) => response.data!
  });
};

export const useSaveTrustDetails = (
  options?: UseDisplayErrorMutationOptions<Result, TrustDetailsPostRequest>
) => {
  const { trustDetailsApi } = useApiClient();
  const invalidateTrustDetails = useInvalidateTrustDetails();
  const invalidateClientDetails = useInvalidateClientDetails();
  const invalidateProposalInformation = useInvalidateProposalInformation();

  return useMutation({
    mutationKey: trustDetailsKeys.submit(),
    mutationFn: (request: TrustDetailsPostRequest) => trustDetailsApi.trustDetailsPost(request),
    onSuccess: async (data, variables, ctx) => {
      if (data.resultState === ResultStates.DisplayError) {
        options?.onDisplayError?.(data);
      } else {
        await Promise.allSettled([
          invalidateTrustDetails(),
          invalidateClientDetails(),
          invalidateProposalInformation()
        ]);
        options?.onSuccess?.(data, variables, ctx);
      }
    }
  });
};
